import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Loader2,
  Mail, CreditCard,
  Cloud, Eye, EyeOff, CheckCircle, XCircle, TestTube, Save,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Sidebar from '../components/Sidebar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConfigField = ({ label, configKey, value, configured, secret, onChange, placeholder }) => {
  const [showSecret, setShowSecret] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor={configKey} className="text-sm font-medium">
          {label}
        </Label>
        <div className="flex items-center gap-2">
          {configured ? (
            <span className="flex items-center gap-1 text-xs text-green-500">
              <CheckCircle className="w-3 h-3" />
              Skonfigurowany
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <XCircle className="w-3 h-3" />
              Nie skonfigurowany
            </span>
          )}
        </div>
      </div>
      <div className="relative">
        <Input
          id={configKey}
          type={secret && !showSecret ? 'password' : 'text'}
          value={value}
          onChange={(e) => onChange(configKey, e.target.value)}
          placeholder={placeholder || `Wpisz ${label.toLowerCase()}`}
          className="pr-10"
        />
        {secret && (
          <button
            type="button"
            onClick={() => setShowSecret(!showSecret)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
};

export default function ConfigPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [config, setConfig] = useState({});
  const [editedValues, setEditedValues] = useState({});
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    fetchConfig();
    fetchProviders();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`);
      setConfig(response.data.config);
      // Initialize edited values with current config (empty for masked secrets)
      const initial = {};
      for (const key of response.data.keys) {
        initial[key] = '';
      }
      setEditedValues(initial);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Brak uprawnien — wymagana rola administratora');
        navigate('/dashboard');
      } else {
        toast.error('Blad ladowania konfiguracji');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/config/ai-providers`);
      setProviders(response.data.providers || []);
    } catch (error) {
      // Fallback providers list
      setProviders([
        { id: 'openai', name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini'], default_model: 'gpt-4o-mini', base_url: 'https://api.openai.com/v1', note: '' },
        { id: 'gemini', name: 'Google Gemini', models: ['gemini-2.0-flash', 'gemini-1.5-pro'], default_model: 'gemini-2.0-flash', base_url: 'https://generativelanguage.googleapis.com/v1beta/openai/', note: '' },
        { id: 'qwen', name: 'Alibaba Qwen', models: ['qwen-plus', 'qwen-turbo'], default_model: 'qwen-plus', base_url: 'https://dashscope.aliyuncs.com/compatible-mode/v1', note: '' },
        { id: 'deepseek', name: 'DeepSeek', models: ['deepseek-chat'], default_model: 'deepseek-chat', base_url: 'https://api.deepseek.com/v1', note: '' },
        { id: 'ollama', name: 'Ollama (Local)', models: ['llama3.1', 'mistral'], default_model: 'llama3.1', base_url: 'http://localhost:11434/v1', note: '' },
        { id: 'custom', name: 'Custom', models: [], default_model: '', base_url: '', note: '' },
      ]);
    }
  };

  const handleChange = (key, value) => {
    setEditedValues(prev => ({ ...prev, [key]: value }));
  };

  const saveSection = async (keys) => {
    setSaving(true);
    const configs = {};
    for (const key of keys) {
      if (editedValues[key] && editedValues[key].trim()) {
        configs[key] = editedValues[key].trim();
      }
    }

    if (Object.keys(configs).length === 0) {
      toast.info('Brak zmian do zapisania');
      setSaving(false);
      return;
    }

    try {
      await axios.put(`${API}/config/batch`, { configs });
      toast.success('Konfiguracja zapisana');
      // Refresh to see updated status
      await fetchConfig();
      // Clear edited values for saved keys
      setEditedValues(prev => {
        const next = { ...prev };
        for (const key of Object.keys(configs)) {
          next[key] = '';
        }
        return next;
      });
    } catch (error) {
      toast.error('Blad zapisu konfiguracji');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (service) => {
    setTesting(prev => ({ ...prev, [service]: true }));
    try {
      const response = await axios.post(`${API}/config/test/${service}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Test ${service} nie powiodl sie`);
    } finally {
      setTesting(prev => ({ ...prev, [service]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/config" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        <div className="mb-10">
          <h1 className="text-3xl font-bold mb-1">
            {language === 'pl' ? 'Konfiguracja systemu' : 'System Configuration'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'pl' ? 'Klucze API i ustawienia uslug zewnetrznych' : 'API keys and external service settings'}
          </p>
        </div>

        <Tabs defaultValue="stripe" className="space-y-6">
          <TabsList className="bg-secondary">
            <TabsTrigger value="stripe" className="gap-2">
              <CreditCard className="w-4 h-4" />
              Stripe
            </TabsTrigger>
            <TabsTrigger value="smtp" className="gap-2">
              <Mail className="w-4 h-4" />
              Email / SMTP
            </TabsTrigger>
            <TabsTrigger value="ai" className="gap-2">
              <Sparkles className="w-4 h-4" />
              AI
            </TabsTrigger>
            <TabsTrigger value="hetzner" className="gap-2">
              <Cloud className="w-4 h-4" />
              Hetzner Storage
            </TabsTrigger>
          </TabsList>

          {/* Stripe */}
          <TabsContent value="stripe">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {language === 'pl' ? 'Platnosci Stripe' : 'Stripe Payments'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? 'Skonfiguruj Stripe aby przyjmowac platnosci od klientow. Klucze znajdziesz w panelu Stripe → Developers → API keys.'
                      : 'Configure Stripe to accept payments from clients. Find your keys in Stripe Dashboard → Developers → API keys.'}
                  </p>
                  <ConfigField
                    label="Publishable Key"
                    configKey="stripe_publishable_key"
                    value={editedValues.stripe_publishable_key}
                    configured={config.stripe_publishable_key__configured}
                    secret={false}
                    onChange={handleChange}
                    placeholder="pk_live_..."
                  />
                  <ConfigField
                    label="Secret Key"
                    configKey="stripe_secret_key"
                    value={editedValues.stripe_secret_key}
                    configured={config.stripe_secret_key__configured}
                    secret={true}
                    onChange={handleChange}
                    placeholder="sk_live_..."
                  />
                  <ConfigField
                    label="Webhook Secret"
                    configKey="stripe_webhook_secret"
                    value={editedValues.stripe_webhook_secret}
                    configured={config.stripe_webhook_secret__configured}
                    secret={true}
                    onChange={handleChange}
                    placeholder="whsec_..."
                  />
                  <div className="flex gap-3 pt-4">
                    <Button onClick={() => saveSection(['stripe_publishable_key', 'stripe_secret_key', 'stripe_webhook_secret'])} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Zapisz
                    </Button>
                    <Button variant="outline" onClick={() => testConnection('stripe')} disabled={testing.stripe}>
                      {testing.stripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                      Testuj polaczenie
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* SMTP */}
          <TabsContent value="smtp">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    {language === 'pl' ? 'Konfiguracja Email (SMTP)' : 'Email Configuration (SMTP)'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? 'Skonfiguruj SMTP do wysylania maili (galeria gotowa, potwierdzenia zamowien, kontrakty). Dziala z Gmail, SendGrid, Mailgun, itp.'
                      : 'Configure SMTP for sending emails (gallery ready, order confirmations, contracts). Works with Gmail, SendGrid, Mailgun, etc.'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <ConfigField
                      label="SMTP Host"
                      configKey="smtp_host"
                      value={editedValues.smtp_host}
                      configured={config.smtp_host__configured}
                      secret={false}
                      onChange={handleChange}
                      placeholder="smtp.gmail.com"
                    />
                    <ConfigField
                      label="SMTP Port"
                      configKey="smtp_port"
                      value={editedValues.smtp_port}
                      configured={config.smtp_port__configured}
                      secret={false}
                      onChange={handleChange}
                      placeholder="587"
                    />
                  </div>
                  <ConfigField
                    label={language === 'pl' ? 'Nazwa uzytkownika' : 'Username'}
                    configKey="smtp_user"
                    value={editedValues.smtp_user}
                    configured={config.smtp_user__configured}
                    secret={false}
                    onChange={handleChange}
                    placeholder="your@email.com"
                  />
                  <ConfigField
                    label={language === 'pl' ? 'Haslo / App Password' : 'Password / App Password'}
                    configKey="smtp_pass"
                    value={editedValues.smtp_pass}
                    configured={config.smtp_pass__configured}
                    secret={true}
                    onChange={handleChange}
                    placeholder="App Password"
                  />
                  <ConfigField
                    label={language === 'pl' ? 'Adres nadawcy (From)' : 'From Address'}
                    configKey="smtp_from"
                    value={editedValues.smtp_from}
                    configured={config.smtp_from__configured}
                    secret={false}
                    onChange={handleChange}
                    placeholder="noreply@yourdomain.com"
                  />
                  <div className="flex gap-3 pt-4">
                    <Button onClick={() => saveSection(['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'smtp_from'])} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Zapisz
                    </Button>
                    <Button variant="outline" onClick={() => testConnection('smtp')} disabled={testing.smtp}>
                      {testing.smtp ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                      Wyslij testowy email
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* AI */}
          <TabsContent value="ai">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    {language === 'pl' ? 'Dostawca AI / LLM' : 'AI / LLM Provider'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? 'Wybierz dostawce AI do Smart Delivery, SEO, Copilota i analizy zdjec. Wszystkie uzywaja kompatybilnego API (OpenAI-compatible).'
                      : 'Choose an AI provider for Smart Delivery, SEO, Copilot, and photo analysis. All use OpenAI-compatible API.'}
                  </p>

                  {/* Provider Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      {language === 'pl' ? 'Dostawca' : 'Provider'}
                    </Label>
                    <select
                      value={editedValues.ai_provider || config.ai_provider || 'openai'}
                      onChange={(e) => {
                        const prov = e.target.value;
                        handleChange('ai_provider', prov);
                        // Auto-fill model and base_url from provider preset
                        const preset = providers.find(p => p.id === prov);
                        if (preset) {
                          handleChange('ai_model', preset.default_model);
                          handleChange('ai_base_url', preset.base_url);
                        }
                      }}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {providers.map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Model Selection */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Model</Label>
                    {(() => {
                      const currentProvider = editedValues.ai_provider || config.ai_provider || 'openai';
                      const preset = providers.find(p => p.id === currentProvider);
                      const models = preset?.models || [];
                      if (models.length > 0) {
                        return (
                          <select
                            value={editedValues.ai_model || config.ai_model || preset?.default_model || ''}
                            onChange={(e) => handleChange('ai_model', e.target.value)}
                            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {models.map(m => (
                              <option key={m} value={m}>{m}{m === preset?.default_model ? ' (domyslny)' : ''}</option>
                            ))}
                          </select>
                        );
                      }
                      return (
                        <Input
                          value={editedValues.ai_model || ''}
                          onChange={(e) => handleChange('ai_model', e.target.value)}
                          placeholder="nazwa-modelu"
                        />
                      );
                    })()}
                  </div>

                  {/* Base URL */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Base URL (API Endpoint)</Label>
                    <Input
                      value={editedValues.ai_base_url || config.ai_base_url || providers.find(p => p.id === (editedValues.ai_provider || config.ai_provider || 'openai'))?.base_url || ''}
                      onChange={(e) => handleChange('ai_base_url', e.target.value)}
                      placeholder="https://api.openai.com/v1"
                    />
                    <p className="text-xs text-muted-foreground">
                      {language === 'pl'
                        ? 'Wypelnia sie automatycznie na podstawie dostawcy. Zmien tylko jesli uzywasz proxy lub custom endpoint.'
                        : 'Auto-filled from provider. Only change if using a proxy or custom endpoint.'}
                    </p>
                  </div>

                  {/* API Key */}
                  <ConfigField
                    label="API Key"
                    configKey="ai_api_key"
                    value={editedValues.ai_api_key}
                    configured={config.ai_api_key__configured}
                    secret={true}
                    onChange={handleChange}
                    placeholder={
                      (editedValues.ai_provider || config.ai_provider || 'openai') === 'openai' ? 'sk-...' :
                      (editedValues.ai_provider || config.ai_provider) === 'gemini' ? 'AIza...' :
                      (editedValues.ai_provider || config.ai_provider) === 'qwen' ? 'sk-...' :
                      'API Key'
                    }
                  />

                  {/* Provider note */}
                  {(() => {
                    const prov = providers.find(p => p.id === (editedValues.ai_provider || config.ai_provider || 'openai'));
                    return prov?.note ? (
                      <div className="bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 text-sm p-3 rounded-md">
                        {prov.note}
                      </div>
                    ) : null;
                  })()}

                  <div className="flex gap-3 pt-4">
                    <Button onClick={() => saveSection(['ai_provider', 'ai_api_key', 'ai_model', 'ai_base_url'])} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      {language === 'pl' ? 'Zapisz' : 'Save'}
                    </Button>
                    <Button variant="outline" onClick={() => testConnection('ai')} disabled={testing.ai}>
                      {testing.ai ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                      {language === 'pl' ? 'Testuj polaczenie' : 'Test Connection'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Current Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {language === 'pl' ? 'Aktualny status AI' : 'Current AI Status'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">{language === 'pl' ? 'Dostawca' : 'Provider'}:</span>
                      <p className="font-medium">{providers.find(p => p.id === (config.ai_provider || 'openai'))?.name || 'OpenAI'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Model:</span>
                      <p className="font-medium">{config.ai_model || 'gpt-4o-mini'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">API Key:</span>
                      <p className="font-medium">{config.ai_api_key__configured ? (
                        <span className="text-green-500 flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Skonfigurowany</span>
                      ) : (
                        <span className="text-red-500 flex items-center gap-1"><XCircle className="w-3 h-3" /> Brak</span>
                      )}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Base URL:</span>
                      <p className="font-medium truncate text-xs">{config.ai_base_url || 'domyslny'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Hetzner */}
          <TabsContent value="hetzner">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cloud className="w-5 h-5" />
                    Hetzner Object Storage
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground">
                    {language === 'pl'
                      ? 'Hetzner Object Storage do przechowywania zdjec w chmurze. Tanszy niz AWS S3, serwery w Europie.'
                      : 'Hetzner Object Storage for cloud photo storage. Cheaper than AWS S3, servers in Europe.'}
                  </p>
                  <ConfigField
                    label="Storage URL (Endpoint)"
                    configKey="hetzner_storage_url"
                    value={editedValues.hetzner_storage_url}
                    configured={config.hetzner_storage_url__configured}
                    secret={false}
                    onChange={handleChange}
                    placeholder="https://fsn1.your-objectstorage.com"
                  />
                  <ConfigField
                    label="API Key / Access Key"
                    configKey="hetzner_api_key"
                    value={editedValues.hetzner_api_key}
                    configured={config.hetzner_api_key__configured}
                    secret={true}
                    onChange={handleChange}
                    placeholder="Hetzner API Key"
                  />
                  <div className="flex gap-3 pt-4">
                    <Button onClick={() => saveSection(['hetzner_storage_url', 'hetzner_api_key'])} disabled={saving}>
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Zapisz
                    </Button>
                    <Button variant="outline" onClick={() => testConnection('hetzner')} disabled={testing.hetzner}>
                      {testing.hetzner ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                      Testuj polaczenie
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
